import T from 'i18n-react';
import moment from 'moment';
import type { PlanTermWorkingTimeProps } from './context';

type getTermTextProps = {
  startDate: moment.MomentInput,
  endDate: moment.MomentInput,
  format?: string,
  formatWithoutYear?: string,
};

export const getTermText = ({
  startDate,
  endDate,
  format = T.translate('moment.date'),
  formatWithoutYear = T.translate('moment.dateWithoutYear'),
}: getTermTextProps = {}): React.ReactNode => {
  const sMoment = moment(startDate);
  const eMoment = moment(endDate);

  if (sMoment.isSame(eMoment, 'day')) {
    return sMoment.format(format);
  }

  const start = sMoment.isValid() ? sMoment.format(format) : '';
  const end = eMoment.isValid()
    ? eMoment.format(
        sMoment.isSame(eMoment, 'year') ? formatWithoutYear : format,
      )
    : '';
  return T.translate('plan.termText', { start, end });
};

type getDaysDurationProps = {
  startDate: moment.MomentInput,
  endDate: moment.MomentInput,
  includeSameDay?: boolean,
};

export const getDaysDuration = ({
  startDate,
  endDate,
  includeSameDay,
}: getDaysDurationProps = {}): number => {
  const sMoment = moment(startDate);
  const eMoment = moment(endDate);

  if (sMoment.isValid() && eMoment.isValid()) {
    const days = eMoment.diff(sMoment, 'days');
    return Math.max(days + (includeSameDay ? 1 : 0), 0);
  }

  return 0;
};

export const getDaysText = (
  dates: Array<{ date: moment.MomentInput }> = [],
): string => {
  const dayFormat = T.translate('moment.dateWithoutYear');
  const dateFormat = T.translate('moment.date');

  const years = [];
  return dates
    .sort((a, b) => moment(a.date) - moment(b.date))
    .map(({ date }) => {
      const m = moment(date);
      if (years.includes(m.year())) {
        return m.format(dayFormat);
      } else {
        years.push(m.year());
        return m.format(dateFormat);
      }
    })
    .join(', ');
};

export const getTotalScheduleMinutes = (
  workingTimes: Array<PlanTermWorkingTimeProps>,
) => {
  return workingTimes.reduce((prev, time) => {
    const startTime = moment(time.startTime).year(0).month(0).day(0);
    const endTime = moment(time.endTime).year(0).month(0).day(0);
    const minutes = moment.duration(endTime.diff(startTime)).asMinutes();
    return prev + (minutes > 0 ? minutes : 0);
  }, 0);
};

export const getTotalWorkingHours = (
  totalTimes: string,
  workingDays: number,
) => {
  if (!moment(totalTimes, 'HH:mm')?.isValid())
    return `0${T.translate('hours')}`;

  const time = moment(totalTimes, 'HH:mm');
  const totalMinutes = (time.hour() * 60 + time.minute()) * workingDays;
  const hours = Math.floor(totalMinutes / 60);
  const hoursText = `${hours}${T.translate('hours')}`;
  const minutes = totalMinutes - Math.floor(hours * 60);
  const minutesText = minutes > 0 ? `${minutes}${T.translate('minutes')}` : '';
  return `${hoursText}${minutesText}`;
};
